import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { track, trackOnce } from '@/lib/analytics';
import { reportSignup } from '@/lib/conversions';
import { links } from '@/lib/config';
import { isPlausiblePhone, joinPhone } from '@/lib/phone';
import { errorMessage } from '@/lib/utils';
import { detectSignupCountry } from '@/lib/signup-country';
import { setPendingSignupEmail } from '@/lib/signup-pending';
import { saveSignupLead } from '@/lib/communication';
import { callingCodeForCountry, CALLING_CODE_OPTIONS } from '@shared/calling-codes';
import { COMPANY_SIZE_LABELS, COMPANY_SIZES, type CompanySize } from '@shared/signup';
import {
  getPendingGoogleSignup,
  setPendingGoogleSignup,
  type GoogleSignupIdentity,
} from '@/lib/google-signup';
import { formatStorageGb, TRIAL } from '@shared/pricing';
import { Button } from '@/components/ui/button';
import { Field, Input, Select } from '@/components/ui/form';
import { inAppBrowser } from '@/lib/in-app-browser';
import { AuthLayout, FormError, OrDivider } from './AuthLayout';
import { GoogleButton, GoogleInBrowser } from './GoogleButton';

type Errors = Partial<Record<'name' | 'email' | 'phone' | 'companySize', string>>;

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [google, setGoogle] = useState<GoogleSignupIdentity | null>(getPendingGoogleSignup);
  const [step, setStep] = useState(() => (getPendingGoogleSignup() ? 1 : 0));
  useEffect(() => {
    setPendingGoogleSignup(null);
  }, []);
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    formRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, [step]);
  const [name, setName] = useState(google?.name ?? '');
  const [email, setEmail] = useState(google?.email ?? params.get('email') ?? '');
  const [companySize, setCompanySize] = useState<CompanySize | ''>('');
  const [company, setCompany] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const phoneCountryEdited = useRef(false);
  useEffect(() => {
    let active = true;
    detectSignupCountry().then((country) => {
      const code = callingCodeForCountry(country);
      if (active && code && !phoneCountryEdited.current) setCountryCode(code);
    });
    return () => {
      active = false;
    };
  }, []);
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  // A field's message goes as soon as it is edited; the next submit checks it again.
  const clearError = (field: keyof Errors) =>
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    trackOnce(
      `signup-view:${step}:${google ? 'google' : 'email'}`,
      'signup_step_view',
      { step: step === 0 ? 'email' : 'profile', method: google ? 'google' : 'email' },
      false,
    );
  }, [step, !!google]);

  const validate = (): Errors => {
    const next: Errors = {};
    if (step === 1 && !name.trim()) next.name = 'Enter your name.';
    if (step === 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      next.email = 'Enter a valid email address.';
    if (step === 1 && (!isPlausiblePhone(joinPhone(countryCode, phone)) || !isPlausiblePhone(phone)))
      next.phone = 'Enter a valid phone number.';
    if (step === 1 && !companySize) next.companySize = 'Select your company size.';
    return next;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setFormError(null);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      track('signup_validation_failed', {
        step: step === 0 ? 'email' : 'profile',
        reason: Object.keys(found)[0],
      });
      const form = e.currentTarget;
      requestAnimationFrame(() => form.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
      return;
    }

    if (step < 1) {
      track('signup_start', { method: 'email' });
      saveSignupLead({ email: email.trim() });
      setStep(step + 1);
      return;
    }
    setBusy(true);
    track('signup_submit', { method: google ? 'google' : 'email', company_size: companySize });
    const lead = {
      email: email.trim(),
      name,
      company,
      phone: joinPhone(countryCode, phone),
      company_size: companySize,
    };
    try {
      if (google) {
        await api.auth.signInWithGoogle(google.credential, google.nonce, 'signup', {
          name,
          company,
          phone: joinPhone(countryCode, phone),
          companySize: companySize as CompanySize,
          // Agreed by creating the account, as the line under the button says.
          terms: true,
        });
        track('email_verified', { method: 'google', source: 'signup' });
        saveSignupLead(lead);
        void reportSignup({ method: 'google', company_size: companySize });
        setBusy(false);
        return;
      }
      const { needsConfirmation } = await api.auth.signUp({
        email,
        name,
        company,
        companySize: companySize as CompanySize,
        phone: joinPhone(countryCode, phone),
      });
      saveSignupLead(lead);
      if (needsConfirmation) {
        track('email_verification_sent', { method: 'email', source: 'signup' });
        // The code screen lives outside GuestOnly: verifying creates a session before the password exists.
        setPendingSignupEmail(email.trim());
        navigate(
          { pathname: '/signup/verify', search: params.toString() ? `?${params}` : '' },
          { state: { email: email.trim(), sent: Date.now() } },
        );
      } else track('sign_up', { method: 'email' });
      // Otherwise the new session redirects straight into setup.
    } catch (err) {
      track('signup_failed', { method: google ? 'google' : 'email', reason: 'request_failed' });
      setFormError(errorMessage(err));
      setBusy(false);
    }
  };

  const loginHref = params.toString() ? `/login?${params}` : '/login';
  // In Facebook / Instagram browsers Google sign-in is blocked, so email comes first and Google moves below.
  const inApp = inAppBrowser();

  return (
    <AuthLayout
      title={['Start your free trial', 'Tell us about yourself'][step]}
      subtitle={
        step === 0
          ? `${TRIAL.days} days free on ${TRIAL.users} phones, with ${formatStorageGb(TRIAL.storageGb)} of recordings. No card needed.`
          : google
            ? 'Complete your details to start your free trial.'
            : 'We’ll email you a code to verify your email, then you’ll set a password.'
      }
    >
      {step === 0 && !inApp && (
        <>
          <GoogleButton
            text="continue_with"
            intent="signup"
            onSignup={(identity) => {
              track('signup_start', { method: 'google' });
              saveSignupLead({ email: identity.email, name: identity.name });
              setGoogle(identity);
              setEmail(identity.email);
              setName(identity.name);
              setStep(1);
              setErrors({});
              setFormError(null);
            }}
          />
          <OrDivider />
        </>
      )}
      {step > 0 && (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-2 text-body">
          <span className="min-w-0 truncate text-fg-muted" title={email}>
            {email.trim()}
          </span>
          <Button
            variant="link"
            size="sm"
            disabled={busy}
            onClick={() => {
              setStep(0);
              setGoogle(null);
              setErrors({});
              setFormError(null);
            }}
          >
            Change
          </Button>
        </div>
      )}

      <form ref={formRef} onSubmit={submit} className="space-y-4" noValidate>
        {formError && (
          <FormError>
            {formError}
            {/already exists/i.test(formError) && (
              <Link to={loginHref} className="mt-1 block font-medium underline">
                Go to sign in
              </Link>
            )}
          </FormError>
        )}

        {step === 0 && (
          <>
            <Field label="Email address" error={errors.email}>
              {(id, d) => (
                <Input
                  id={id}
                  aria-describedby={d}
                  aria-invalid={!!errors.email}
                  autoFocus
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  className="h-12"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors({});
                  }}
                />
              )}
            </Field>
          </>
        )}

        {step === 1 && (
          <fieldset disabled={busy} className="space-y-4">
            <Field label="Your name" error={errors.name}>
              {(id, d) => (
                <Input
                  id={id}
                  aria-describedby={d}
                  aria-invalid={!!errors.name}
                  autoComplete="name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError('name');
                  }}
                />
              )}
            </Field>

            <Field label="Company" optional>
              {(id) => (
                <Input
                  id={id}
                  autoComplete="organization"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              )}
            </Field>

            <Field label="Company size" error={errors.companySize}>
              {(id, d) => (
                <Select
                  id={id}
                  aria-describedby={d}
                  aria-invalid={!!errors.companySize}
                  value={companySize}
                  onChange={(e) => {
                    setCompanySize(e.target.value as CompanySize | '');
                    clearError('companySize');
                  }}
                >
                  <option value="" disabled>
                    Select company size
                  </option>
                  {COMPANY_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {COMPANY_SIZE_LABELS[size]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="WhatsApp number" error={errors.phone}>
              {(id, d) => (
                <div className="flex gap-2">
                  <select
                    aria-label="Country code"
                    value={countryCode}
                    onChange={(e) => {
                      phoneCountryEdited.current = true;
                      setCountryCode(e.target.value);
                      clearError('phone');
                    }}
                    className="field-control h-9 w-20 shrink-0 bg-surface px-2 text-body text-fg outline-none"
                  >
                    {CALLING_CODE_OPTIONS.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                  <Input
                    id={id}
                    aria-describedby={d}
                    aria-invalid={!!errors.phone}
                    type="tel"
                    autoComplete="tel-national"
                    inputMode="tel"
                    placeholder="98765 43210"
                    value={phone}
                    onChange={(e) => {
                      phoneCountryEdited.current = true;
                      setPhone(e.target.value);
                      clearError('phone');
                    }}
                  />
                </div>
              )}
            </Field>

          </fieldset>
        )}
        <Button type="submit" size="lg" className="h-12 w-full" loading={busy}>
          {step === 1 ? 'Create account' : 'Continue'}
          {step < 1 && <ArrowRight className="size-4" aria-hidden="true" />}
        </Button>
        {step > 0 && (
          <Button
            variant="ghost"
            className="w-full"
            disabled={busy}
            onClick={() => {
              setStep(step - 1);
              setGoogle(null);
              setErrors({});
              setFormError(null);
            }}
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back
          </Button>
        )}
      </form>
      {step === 0 && inApp && (
        <>
          <OrDivider />
          <GoogleInBrowser intent="signup" />
        </>
      )}

      <p className="mt-6 text-center text-sm text-fg-muted">
        Already have an account?{' '}
        <Link to={loginHref} className="font-medium text-accent-fg hover:underline">
          Sign in
        </Link>
      </p>
      {/* One consent line on both steps; a second checkbox on the profile step only added a click. */}
      <p className="mt-8 text-center text-xs text-fg-subtle">
        By creating an account, you agree to our{' '}
        <a href={links.terms} target="_blank" rel="noreferrer" className="underline">
          Terms
        </a>{' '}
        and{' '}
        <a href={links.privacy} target="_blank" rel="noreferrer" className="underline">
          Privacy Policy
        </a>
        .
      </p>
    </AuthLayout>
  );
}
